import type { EffortTier } from "./mcp-chat-types";

/**
 * Presentation + persistence for the user-facing model "effort" tier
 * (Lite / Economy / Power). Shared by the panel (tab defaults + send payload)
 * and the input-area selector UI so labels/descriptions never drift.
 *
 * The concrete server-side model ids live in mcp-chat-server
 * (providers/effort-tiers.ts); the client only knows the friendly names.
 */

export const EFFORT_ORDER: readonly EffortTier[] = ["lite", "economy", "power"] as const;

export const DEFAULT_EFFORT: EffortTier = "lite";

export const EFFORT_LABELS: Record<EffortTier, string> = {
  lite: "Lite",
  economy: "Economy",
  power: "Power"
};

export const EFFORT_DESCRIPTIONS: Record<EffortTier, string> = {
  lite: "Fast and economical. Handles everyday builds and edits — lighter on deep, multi-step reasoning. Best default for most work.",
  economy: "Balanced quality and cost. Stronger reasoning for multi-screen builds and trickier logic.",
  power:
    "Top quality for the hardest tasks — best reasoning and design depth. Noticeably more expensive; use when it matters."
};

const EFFORT_STORAGE_KEY = "kuika_mcp_effort";

function isEffortTier(value: unknown): value is EffortTier {
  return value === "lite" || value === "economy" || value === "power";
}

/** Last chosen tier (persisted), falling back to the default. New tabs seed from this. */
export function getStoredEffort(): EffortTier {
  try {
    const raw: string | null = localStorage.getItem(EFFORT_STORAGE_KEY);
    if (isEffortTier(raw)) return raw;
  } catch {
    // localStorage unavailable (private mode / SSR) — fall through to default.
  }
  return DEFAULT_EFFORT;
}

export function setStoredEffort(effort: EffortTier): void {
  try {
    localStorage.setItem(EFFORT_STORAGE_KEY, effort);
  } catch {
    // Best-effort persistence — a write failure must not break sending.
  }
}
